import { useRef, useState } from 'react';
import { Upload, X, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAttachmentUpload } from '@/hooks/useAttachmentUpload';
import { cn } from '@/lib/utils';
import { AttachmentImage } from './AttachmentImage';
import { toast } from 'sonner';

interface AttachmentUploaderProps {
  onUploadComplete: (filePath: string) => void;
  onRemove: (filePath: string) => void;
  attachments: string[];
  maxFiles?: number;
}

export const AttachmentUploader = ({ 
  onUploadComplete, 
  onRemove, 
  attachments,
  maxFiles = 5 
}: AttachmentUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, uploading, uploadProgress } = useAttachmentUpload();
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    let successCount = 0;
    let failCount = 0;
    
    for (const file of files) {
      if (attachments.length + successCount >= maxFiles) {
        toast.warning(`Limite de ${maxFiles} arquivos atingido`);
        break;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls(prev => ({ ...prev, [file.name]: reader.result as string }));
      };
      reader.readAsDataURL(file);

      // Upload
      const filePath = await uploadFile(file);
      if (filePath) {
        onUploadComplete(filePath);
        successCount++;
      } else {
        failCount++;
      }
    }

    // Feedback claro ao usuário
    if (successCount > 0) {
      toast.success(
        successCount === 1 
          ? 'Imagem anexada com sucesso!' 
          : `${successCount} imagens anexadas com sucesso!`
      );
    }
    if (failCount > 0) {
      toast.error(
        failCount === 1 
          ? 'Falha ao enviar 1 imagem' 
          : `Falha ao enviar ${failCount} imagens`
      );
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canUploadMore = attachments.length < maxFiles;

  return (
    <div className="space-y-3">
      {/* Upload Button */}
      {canUploadMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando... {uploadProgress}%
              </>
            ) : attachments.length > 0 ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                {attachments.length} anexo(s) • Adicionar mais
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Adicionar Imagens
              </>
            )}
          </Button>
          <p className="text-xs text-white/40 mt-1">
            Máximo {maxFiles} arquivos • JPG, PNG ou WEBP • 5MB cada
          </p>
        </div>
      )}

      {/* Preview Grid */}
      {attachments.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {attachments.map((path, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10">
              <AttachmentImage
                filePath={path}
                alt={`Anexo ${index + 1}`}
                className="w-full h-full object-cover"
                containerClassName="w-full h-full bg-white/5"
              />
              <button
                type="button"
                onClick={() => onRemove(path)}
                className={cn(
                  'absolute top-1 right-1 w-6 h-6 rounded-full',
                  'bg-black/80 hover:bg-black/90 transition-colors',
                  'flex items-center justify-center'
                )}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
